/*
 * Pre-order market scoping — Shopify reports selling_plan_allocations with no market dimension, so an
 * offer created for one market appears in all of them. Reads the pre-order app's per-market plan map
 * and prunes both the pre-order state and the ship date to the current market.
 *
 * Loaded defer from the head so it sees the app's inline config and still runs before the product
 * section's script. An excluded variant is marked unavailable, taking the theme's out-of-stock route.
 *
 * See docs/orchestrations/orch_PREORDER_MARKET_SCOPE.md.
 */
;(function () {
  'use strict';

  function warn(message) {
    if (window.console && typeof console.warn === 'function') {
      console.warn('[preorder-market-scope] ' + message);
    }
  }

  // ── Config read (three-state ladder) ──────────────────────────────────────
  // marketId comes from Shopify's own server-rendered value; the app's is a cross-check only.
  function readConfig() {
    var cfg = window._ReStockConfig;
    if (!cfg) return { state: 'absent' };
    // The app delivers the payload at _metafields.preOrderConfig during parsing; its loader later moves
    // it to pre_order. The two are mutually exhaustive, so whichever is populated is the payload.
    var po = (cfg._metafields && cfg._metafields.preOrderConfig) || cfg.pre_order || {};
    var map = po.selling_plan_id_to_market_ids;
    var mine = String(window.BroraMarketId || cfg.marketId || '');
    var ok = map && typeof map === 'object' && Object.keys(map).length > 0 && mine !== '';
    if (!ok) return { state: 'partial' };
    // Cross-check only: a mismatch means Shopify and the app disagree about the market.
    if (cfg.marketId && String(cfg.marketId) !== mine) warn('market id mismatch');
    return { state: 'ok', map: map, market: mine, po: po };
  }

  var CFG = readConfig();
  if (CFG.state === 'absent') warn('pre-order market map unavailable — market scoping inactive');
  if (CFG.state === 'partial') warn('pre-order market map unreadable — pre-order suppressed');

  // ── The market predicate ──────────────────────────────────────────────────
  // absent  → fail OPEN  (leave the server-rendered state untouched)
  // partial → fail CLOSED (suppress pre-order everywhere)
  // ok      → the plan's market list must contain this market
  function allowsPlan(planId) {
    if (CFG.state === 'absent') return true;
    if (CFG.state === 'partial') return false;
    if (!planId) return false;
    var ids = CFG.map[String(planId)];
    if (!Array.isArray(ids)) return false; // plan unknown to the app → closed
    return ids.some(function (m) { return String(m) === CFG.market; });
  }

  // Convenience for the /products/<handle>.js consumers (mini-bag, Voyado).
  function anyAllowed(allocations) {
    return (allocations || []).some(function (a) { return allowsPlan(a.selling_plan_id); });
  }
  function allowedPlanId(allocations) {
    var a = (allocations || []).filter(function (x) { return allowsPlan(x.selling_plan_id); })[0];
    return a ? a.selling_plan_id : null;
  }

  // ── Ship date ─────────────────────────────────────────────────────────────
  // String-only: a Date would parse the ISO value as UTC midnight and render a day early in any
  // negative-offset market. Output matches the format Liquid renders.
  function isoToThemeDate(iso) {
    var m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || ''));
    return m ? m[3] + '/' + m[2] + '/' + m[1].slice(2) : '';
  }

  // Returns '' for an unrecognised type, a missing entry or a malformed date, leaving the caller with
  // the metafield date already in the blob.
  function planDate(planId) {
    if (CFG.state !== 'ok' || !planId) return '';
    var cfg = (CFG.po || {}).description_availability_config;
    var e = cfg && cfg[String(planId)];
    if (!e || e.type !== 'SPECIFIC_DATE') return '';
    return isoToThemeDate((e.config || {}).date);
  }

  // ── Unavailable-pill markup ───────────────────────────────────────────────
  // Liquid emits the envelope and strike only for a size it resolved as unavailable, and the styling
  // reserves room for them — so a pill marked unavailable here needs the same two children.
  var templates = {};

  function templateContent(selector) {
    if (!(selector in templates)) {
      var el = document.querySelector(selector);
      templates[selector] = (el && el.content) || null;
    }
    return templates[selector];
  }

  // data-slot marks the Voyado renderer's fill points, so it is dropped from every clone taken here.
  function stripSlots(node) {
    node.removeAttribute('data-slot');
    Array.prototype.forEach.call(node.querySelectorAll('[data-slot]'), function (el) {
      el.removeAttribute('data-slot');
    });
    return node;
  }

  // The icon doubles as the presence guard, so a pill Liquid already rendered unavailable is left
  // alone and repeat calls add nothing.
  function markSizeUnavailable(label) {
    if (label.querySelector('.action-size__icon')) return;
    var content = templateContent('[data-tmpl-card-size]');
    var icon = content && content.querySelector('.action-size__icon');
    var line = content && content.querySelector('.action-size__line');
    if (!icon || !line) return;
    label.appendChild(stripSlots(icon.cloneNode(true)));
    label.appendChild(line.cloneNode(true));
  }

  // An available pill carries neither child.
  function markSizeAvailable(label) {
    var icon = label.querySelector('.action-size__icon');
    var line = label.querySelector('.action-size__line');
    if (icon) icon.remove();
    if (line) line.remove();
  }

  // ── PDP correction: rewrite the JSON blob before the product script parses it ──
  // The blob is the single upstream of the CTA label, the ship-date line and the selling-plan input,
  // so one pass corrects state and date everywhere.
  function gatePdp() {
    if (CFG.state === 'absent') return;
    var el = document.querySelector('[data-product-json]');
    if (!el) return;
    var data;
    try { data = JSON.parse(el.textContent); } catch (_) { return; }
    var plans = data.sellingPlans || {};
    var dates = data.preorder || (data.preorder = {});
    var pruned = [];
    var changed = false;

    Object.keys(plans).forEach(function (vid) {
      if (!allowsPlan(plans[vid])) {
        delete plans[vid];
        pruned.push(vid);
        changed = true;
        return;
      }
      // Plans are already market-scoped, so a plan-keyed date needs no second lookup. An empty result
      // leaves the metafield date in place.
      var d = planDate(plans[vid]);
      if (d && dates[vid] !== d) {
        dates[vid] = d;
        changed = true;
      }
    });

    // An excluded variant has zero stock and no applicable offer, so marking it unavailable takes the
    // theme's out-of-stock route. Its ship date is dropped so no stale date can render.
    if (pruned.length) {
      (data.variants || []).forEach(function (v) {
        if (pruned.indexOf(String(v.id)) >= 0) v.available = false;
      });
      pruned.forEach(function (vid) { delete dates[vid]; });
      // Neither pill group is downstream of this blob, so both are gated here, in a pass that runs
      // before the document is painted: an excluded size is painted in its final state.
      gateSizeGroup('[data-sticky-size-options]', pruned, data);
      gateSizeGroup('[data-size-options]', pruned, data);
      // The sticky bar reads sold-out from an attribute Liquid wrote without a market dimension, so it
      // is restated once nothing is purchasable here.
      if (!(data.variants || []).some(function (v) { return v.available; })) {
        var stickyBar = document.querySelector('[data-sticky-atb]');
        if (stickyBar) stickyBar.setAttribute('data-product-available', 'false');
      }
    }
    if (changed) el.textContent = JSON.stringify(data);
  }

  // ── Size pills ────────────────────────────────────────────────────────────
  // Both pill groups are rendered in Liquid from variant.available, which Shopify reports as true for a
  // continue-selling zero-stock variant, so an excluded size arrives styled and behaving as buyable.
  // Each group is disabled directly: the sticky bar has no availability pass of its own, and gating the
  // buy area here as well means neither group depends on the other having run first.
  //
  // These pills are keyed by size name rather than variant id, so each excluded variant's size label is
  // resolved from the blob's size option position and matched on the radio value.
  function gateSizeGroup(selector, prunedIds, data) {
    var group = document.querySelector(selector);
    if (!group || !prunedIds.length) return;
    var pos = data.sizeOptionPosition || 1;
    (data.variants || []).forEach(function (v) {
      if (prunedIds.indexOf(String(v.id)) < 0) return;
      var size = v['option' + pos];
      if (!size) return;
      var input = group.querySelector('input[type="radio"][value="' + CSS.escape(size) + '"]');
      var label = input && input.closest('label.action-size');
      if (!input || !label) return;
      // The unavailable look is keyed on the label's attribute. The radio is left as each group's own
      // script expects it: an unavailable size stays selectable so the click reaches the notify route,
      // and the buy area's availability pass owns the input state there.
      label.setAttribute('aria-disabled', 'true');
      markSizeUnavailable(label);
    });
  }

  // ── Buy-area size pills ───────────────────────────────────────────────────
  // The product script owns the buy area's availability pass: it reads the corrected blob and re-runs
  // on every colourway change, toggling aria-disabled per pill. The markup therefore follows that
  // attribute rather than being applied once — a pill that gains it gains the icon and strike, a pill
  // that loses it gives them back.
  function watchBuyAreaSizes() {
    var group = document.querySelector('[data-size-options]');
    if (!group) return;
    function sync(label) {
      if (label.hasAttribute('aria-disabled')) markSizeUnavailable(label);
      else markSizeAvailable(label);
    }
    new MutationObserver(function (records) {
      records.forEach(function (record) {
        var label = record.target;
        if (label.nodeType === 1 && label.matches('label.action-size')) sync(label);
      });
    }).observe(group, { attributes: true, attributeFilter: ['aria-disabled'], subtree: true });
    Array.prototype.forEach.call(group.querySelectorAll('label.action-size'), sync);
  }

  // ── Liquid card correction ────────────────────────────────────────────────
  // Card roots for a scope. The scope may be the card itself (re-entrant call after a colourway remap)
  // or an ancestor (a section re-render), so both are collected.
  function cardRoots(scope) {
    var node = scope || document;
    var roots = [];
    if (node.nodeType === 1 && typeof node.matches === 'function' && node.matches('[data-product-card]')) {
      roots.push(node);
    }
    Array.prototype.forEach.call(node.querySelectorAll('[data-product-card]'), function (card) {
      roots.push(card);
    });
    return roots;
  }

  // Every pre-order size pill carries its plan id. A pill whose plan is not offered here is replaced
  // with the out-of-stock pill the card already renders for an out-of-stock size.
  function gateCards(scope) {
    if (CFG.state === 'absent') return;
    cardRoots(scope).forEach(function (card) {
      Array.prototype.forEach.call(card.querySelectorAll('[data-preorder]'), function (pill) {
        if (allowsPlan(pill.getAttribute('data-preorder'))) return;
        // The marker is only dropped once the pill has actually been replaced. A pill that keeps it
        // routes to the product page; a pill stripped of it adds to the bag, so a conversion that
        // could not complete must leave the marker in place.
        if (toOosPill(card, pill)) pill.removeAttribute('data-preorder');
      });
      // The pills cover the active colourway only, the badge covers the whole product, so the badge is
      // gated on its own product-level source rather than on what the pills leave behind.
      gateBadge(card);
    });
  }

  // Reproduce the out-of-stock branch of the quick-add snippet — identical classes, icons, screen-reader
  // copy and link shape — from the out-of-stock size template, and point it at this variant. The link
  // target comes from the card's own out-of-stock pill when it has one, and otherwise from the card's
  // product link, so every card can convert regardless of what its other sizes are doing.
  // Returns whether the pill was replaced, so the caller can keep the pre-order marker when it was not.
  function toOosPill(card, pill) {
    var oos = oosPillFromTemplate();
    if (!oos) return false;
    var id = pill.getAttribute('data-variant-id') || '';
    var anchor = card.querySelector('a.action-size--unavailable') || card.querySelector('a[href*="/products/"]');
    // Through the same allowlist every other non-Liquid href sink in the theme uses, so a copied href
    // cannot introduce a scheme or origin the card did not already carry.
    var raw = anchor ? (anchor.getAttribute('href') || '').split('?')[0] : '';
    var href = raw && window.voyadoSafeUrl ? window.voyadoSafeUrl(raw) : raw;
    if (href) oos.setAttribute('href', id ? href + '?variant=' + encodeURIComponent(id) : href);
    var textEl = pill.querySelector('.action-size__text');
    var oosText = oos.querySelector('.action-size__text');
    if (oosText) oosText.textContent = textEl ? textEl.textContent : pill.textContent.trim();
    if (!pill.parentNode) return false;
    pill.parentNode.replaceChild(oos, pill);
    return true;
  }

  function oosPillFromTemplate() {
    var content = templateContent('[data-tmpl-card-size-oos]');
    var node = content && content.querySelector('a.action-size');
    return node ? stripSlots(node.cloneNode(true)) : null;
  }

  // The badge is a product-level signal: the badges snippet derives it from every variant, while the
  // quick-add pills list only the active colourway's sizes. It is therefore gated on the plan list the
  // card root carries, which spans the same variants the badge itself is derived from. The badge goes
  // only when the product has pre-order plans and none of them is offered here. Nothing replaces it.
  function gateBadge(card) {
    var raw = card.getAttribute('data-card-preorder-plans');
    if (raw === null) return; // no product context — the caller supplied its own sizes
    var plans = raw ? raw.split(',') : [];
    if (!plans.length) return; // no pre-order variant on this product → not our badge
    if (plans.some(allowsPlan)) return; // at least one plan is offered here → the badge is correct
    var badge = card.querySelector('[data-card-badge="pre-order"]');
    if (badge) badge.remove();
  }

  gatePdp();
  watchBuyAreaSizes();
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { gateCards(); }, { once: true });
  } else {
    gateCards();
  }
  document.addEventListener('shopify:section:load', function (e) { gateCards(e.target); });

  // Public API — every method below is called from another file.
  window.BroraPreorderMarket = {
    allowsPlan: allowsPlan,
    anyAllowed: anyAllowed,
    allowedPlanId: allowedPlanId,
    gateCards: gateCards,
    state: function () { return CFG.state; }
  };
})();
